import { createFileRoute, Link } from "@tanstack/react-router";
import { SEOHead } from "@/components/shared/SEOHead";

export const Route = createFileRoute("/support")({
  component: SupportPage,
});

function PageNav() {
  return (
    <nav aria-label="Related pages" className="flex flex-wrap gap-2 mb-10">
      <Link
        to="/privacy"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-surface border border-qds-border text-qds-text-secondary hover:border-qds-accent/40 hover:text-qds-text-primary transition-colors min-h-[44px] inline-flex items-center"
      >
        Privacy Policy
      </Link>
      <Link
        to="/terms"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-surface border border-qds-border text-qds-text-secondary hover:border-qds-accent/40 hover:text-qds-text-primary transition-colors min-h-[44px] inline-flex items-center"
      >
        Terms of Service
      </Link>
      <Link
        to="/support"
        className="px-4 py-1.5 rounded-full text-sm font-medium bg-qds-accent text-qds-text-inverse transition-colors min-h-[44px] inline-flex items-center"
        aria-current="page"
      >
        Support
      </Link>
    </nav>
  );
}

function SupportPage() {
  return (
    <div className="page-enter bp-utility-page bp-legal-page">
      <SEOHead
        title="Support"
        description="Stuck on something? Find answers to common questions or get in touch with the Quuil team."
        path="/support"
      />

      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <PageNav />

        <header className="mb-10">
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-qds-text-primary mb-3">
            Support
          </h1>
          <p className="text-qds-text-secondary text-[15px] leading-relaxed">
            Something not working? Check below first — most answers are here. If not, email us and we'll sort it out.
          </p>
        </header>

        <div className="space-y-10">
          {/* Contact */}
          <section className="bg-qds-accent-tint rounded-xl p-6 border border-qds-accent/15">
            <h2 className="text-lg font-medium text-qds-text-primary mb-2">Reach Out</h2>
            <p className="text-sm text-qds-text-secondary mb-4">
              Email us at{" "}
              <a
                href="mailto:support@quuil.com"
                className="text-qds-accent hover:text-qds-accent-hover font-medium underline underline-offset-2 min-h-auto"
              >
                support@quuil.com
              </a>
              . We usually reply the same day.
            </p>
            <div className="bg-qds-surface-elevated rounded-lg p-4 border border-qds-border">
              <h3 className="text-sm font-medium text-qds-text-primary mb-2">Help us help you faster</h3>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-qds-text-secondary">
                <li>What happened and what you expected instead</li>
                <li>Screenshots if you have them</li>
                <li>Your device and iOS version</li>
              </ul>
            </div>
          </section>

          {/* Common Questions */}
          <section>
            <h2 className="text-xl font-medium text-qds-text-primary mb-5">Quick Answers</h2>
            <div className="space-y-8">
              {/* Purchases */}
              <div>
                <h3 className="text-base font-medium text-qds-text-primary mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-qds-accent/10 flex items-center justify-center shrink-0" aria-hidden="true">
                    <span className="text-qds-accent text-xs font-bold">$</span>
                  </span>
                  Credits &amp; Billing
                </h3>
                <div className="space-y-4 pl-9">
                  <div>
                    <h4 className="text-sm font-medium text-qds-text-primary mb-1">How do I restore my purchases?</h4>
                    <p className="text-sm text-qds-text-secondary leading-relaxed">
                      Open the Quuil app, go to Settings, and tap "Restore Purchases." This will
                      sync any credits or purchases associated with your Apple ID. Make sure
                      you're signed in with the same Apple ID you used for the original purchase.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-qds-text-primary mb-1">How do I get a refund?</h4>
                    <p className="text-sm text-qds-text-secondary leading-relaxed">
                      All purchases are processed through Apple. To request a refund, go to{" "}
                      <a
                        href="https://reportaproblem.apple.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-qds-accent hover:text-qds-accent-hover font-medium underline underline-offset-2 min-h-auto"
                      >
                        reportaproblem.apple.com
                      </a>{" "}
                      or contact Apple Support through your purchase history in the App Store.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-qds-text-primary mb-1">My credits are missing</h4>
                    <p className="text-sm text-qds-text-secondary leading-relaxed">
                      Try restoring purchases first (see above). If credits are still missing,
                      make sure you're signed in with the correct Apple ID. If the issue persists,
                      contact us at{" "}
                      <a
                        href="mailto:support@quuil.com"
                        className="text-qds-accent hover:text-qds-accent-hover font-medium underline underline-offset-2 min-h-auto"
                      >
                        support@quuil.com
                      </a>{" "}
                      with your Apple ID and a screenshot of your purchase receipt.
                    </p>
                  </div>
                </div>
              </div>

              {/* Login and Account */}
              <div>
                <h3 className="text-base font-medium text-qds-text-primary mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-qds-accent/10 flex items-center justify-center shrink-0" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-qds-accent" aria-hidden="true">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                  Your Account
                </h3>
                <div className="space-y-4 pl-9">
                  <div>
                    <h4 className="text-sm font-medium text-qds-text-primary mb-1">How do I sign in?</h4>
                    <p className="text-sm text-qds-text-secondary leading-relaxed">
                      Quuil uses Sign in with Apple for authentication. Tap "Sign in with Apple"
                      on the login screen and follow the prompts. You can choose to share or hide
                      your email address.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-qds-text-primary mb-1">How do I delete my account?</h4>
                    <p className="text-sm text-qds-text-secondary leading-relaxed">
                      Open the Quuil app, go to Settings, scroll down and tap "Delete Account."
                      This will permanently remove your account and all associated data. This
                      action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Troubleshooting */}
              <div>
                <h3 className="text-base font-medium text-qds-text-primary mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-qds-accent/10 flex items-center justify-center shrink-0" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-qds-accent" aria-hidden="true">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                    </svg>
                  </span>
                  Troubleshooting
                </h3>
                <div className="space-y-4 pl-9">
                  <div>
                    <h4 className="text-sm font-medium text-qds-text-primary mb-1">The app is crashing or won't load</h4>
                    <p className="text-sm text-qds-text-secondary leading-relaxed">
                      Run through these in order:
                    </p>
                    <ol className="list-decimal pl-5 mt-1.5 space-y-1 text-sm text-qds-text-secondary">
                      <li>Update to the latest version of Quuil from the App Store</li>
                      <li>Check your internet connection</li>
                      <li>Force quit and reopen (swipe up from the app switcher)</li>
                      <li>Restart your device</li>
                    </ol>
                    <p className="text-sm text-qds-text-secondary mt-1.5">
                      Still stuck? Email us and tell us what you were doing when it happened.
                    </p>
                  </div>
                </div>
              </div>

              {/* Privacy and Data */}
              <div>
                <h3 className="text-base font-medium text-qds-text-primary mb-3 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-qds-accent/10 flex items-center justify-center shrink-0" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-qds-accent" aria-hidden="true">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  Privacy and Data
                </h3>
                <div className="pl-9">
                  <p className="text-sm text-qds-text-secondary leading-relaxed">
                    For details about how we collect, use, and protect your information, please
                    read our{" "}
                    <Link
                      to="/privacy"
                      className="text-qds-accent hover:text-qds-accent-hover font-medium underline underline-offset-2 min-h-auto"
                    >
                      Privacy Policy
                    </Link>
                    . You can delete your account and all associated data at any time through
                    the app's settings.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
